import { Auth0DecodedHash, Auth0Error, Auth0UserProfile, AuthOptions, Management, WebAuth } from "auth0-js";
import { addSeconds } from "date-fns";
import { camelizeKeys, decamelizeKeys } from "humps";
import { autorun, computed, observable, runInAction } from "mobx";
import { fromPromise, IPromiseBasedObservable, now } from "mobx-utils";

import * as logger from "nvent-web/services/logger";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { delay } from "nvent-web/utils/promise";

export interface UserMetadata {
  givenName: string;
  familyName: string;
  phoneNumber: string;
}

export interface IdTokenPayload {
  // user data
  sub: string;
  email: string;
  emailVerified: boolean;

  // Claims transferred from user metadata by Auth0 rules,
  // might not be immediately updated, because Auth0 rules are run asynchronously
  givenName: string;
  familyName: string;
  phoneNumber: string;

  // token metadata
  iat: number;
  iss: string;
  aud: string;
  exp: number;
  nonce: string;
}

export class Auth0 {
  @observable
  loadingPromise: IPromiseBasedObservable<void>;

  @computed
  get isLoading() {
    return this.loadingPromise.state === "pending";
  }

  @observable
  accessToken = "";

  @observable
  accessExpiration = new Date(0);

  @computed
  get accessExpired() {
    return this.accessExpiration.getTime() <= now();
  }

  @computed
  get isAuthenticated() {
    return Boolean(this.accessToken);
  }

  @observable
  thirdPartyCookiesBlocked = false;

  @observable
  idToken = "";

  @observable
  idTokenPayload?: IdTokenPayload;

  @computed
  get accessWillExpireSoon() {
    return this.accessExpiration.getTime() <= soon();
  }

  @observable
  managementToken = "";

  @observable
  managementExpiration = new Date(0);

  @observable
  private managementRefreshFailed = false;

  get management() {
    return new Management({
      domain: this.options.domain,
      token: this.managementToken,
    });
  }

  @computed
  get managementExpired() {
    return this.managementExpiration.getTime() <= now();
  }

  @computed
  get managementWillExpireSoon() {
    return this.managementExpiration.getTime() <= soon();
  }

  private webAuth: WebAuth;
  private readonly connection = "Username-Password-Authentication";

  constructor(
    private baseUrl: string,
    private options: AuthOptions,
    private stores: { notifications: NotificationsStore }
  ) {
    this.webAuth = new WebAuth({
      responseType: "token id_token",
      redirectUri: `${baseUrl}/auth/login/callback`,
      scope: [
        "openid", // sub
        "profile", // given_name, family_name, updated_at
        "email", // email, email_verified
        "phone", // phone_number, phone_number_verified
      ].join(" "),
      ...options,
    });

    this.loadingPromise = fromPromise(this.init());
  }

  signup(email: string, password: string, metadata: UserMetadata): Promise<unknown> {
    return new Promise<unknown>((resolve, reject) => {
      this.webAuth.signup(
        {
          connection: this.connection,
          email,
          password,
          userMetadata: decamelizeKeys(metadata),
        },
        (err, result) => (err ? reject(err) : resolve(result))
      );
    });
  }

  login(email: string, password: string): Promise<unknown> {
    return new Promise<unknown>((resolve, reject) => {
      this.webAuth.login(
        {
          realm: this.connection,
          email,
          password,
        },
        (err, result) => (err ? reject(err) : resolve(result))
      );
    });
  }

  async logout() {
    this.webAuth.logout({
      returnTo: `${this.baseUrl}/auth/logout/callback`,
    });
  }

  changePassword(email: string) {
    return new Promise<unknown>((resolve, reject) => {
      this.webAuth.changePassword(
        {
          connection: this.connection,
          email,
        },
        (err, result) => (err ? reject(err) : resolve(result))
      );
    });
  }

  async updateMetadata(metadata: UserMetadata) {
    if (!this.idTokenPayload) {
      throw new Error("Requires authentication");
    }

    if (!this.managementToken) {
      throw new Error("Requires an Auth0 management API token");
    }

    const id = this.idTokenPayload.sub;
    const data = decamelizeKeys(metadata);

    await new Promise<Auth0UserProfile>((resolve, reject) =>
      this.management.patchUserMetadata(id, data, (err, res) => (err ? reject(err) : resolve(res)))
    );

    // wait for a new hash with an updated id token
    const hash = await this.ensureValidHash(
      await this.checkSession(),
      ({ idTokenPayload }) =>
        idTokenPayload &&
        idTokenPayload.given_name === metadata.givenName &&
        idTokenPayload.family_name === metadata.familyName &&
        idTokenPayload.phone_number === metadata.phoneNumber
    );

    await this.handleHashChanged(hash);
  }

  private async init() {
    let hash: Auth0DecodedHash | null = null;

    try {
      hash = await this.parseHash();
      runInAction(() => (this.thirdPartyCookiesBlocked = false));
    } catch (err) {
      logger.error(err);
      this.stores.notifications.createGenericError(err, "Login error");

      runInAction(
        () =>
          (this.thirdPartyCookiesBlocked =
            isAuth0Error(err) &&
            auth0ErrorCode(err) === "invalid_request" &&
            auth0ErrorDescription(err) === "No verifier returned from client.")
      );
    }

    if (hash) {
      await this.handleHashChanged(hash);
    } else {
      await this.refreshAccessToken();
    }

    // keep tokens up to date
    autorun(() => {
      if (this.accessToken && this.accessWillExpireSoon) {
        this.refreshAccessToken();
      }
    });

    autorun(() => {
      if (this.accessToken && !this.accessExpired && this.managementWillExpireSoon && !this.managementRefreshFailed) {
        this.refreshManagementToken();
      }
    });
  }

  private async handleHashChanged(hash: Auth0DecodedHash | null) {
    hash = await this.ensureValidHash(hash, ({ idTokenPayload }) => idTokenPayload && idTokenPayload.given_name);

    runInAction(() => {
      if (hash) {
        this.accessToken = hash.accessToken!;
        this.accessExpiration = addSeconds(new Date(), hash.expiresIn!);
        this.idToken = hash.idToken!;
        this.idTokenPayload = camelizeKeys(hash.idTokenPayload) as IdTokenPayload;
      } else {
        this.accessToken = "";
        this.accessExpiration = new Date(0);
        this.idToken = "";
        this.idTokenPayload = undefined;
        this.managementToken = "";
        this.managementExpiration = new Date(0);
      }

      this.managementRefreshFailed = false;
    });
  }

  private async refreshAccessToken() {
    let hash: Auth0DecodedHash | null;

    try {
      hash = await this.checkSession();
    } catch (error) {
      hash = handleError(error, { login_required: () => null });
    }

    await this.handleHashChanged(hash);
  }

  private async refreshManagementToken() {
    let hash: Auth0DecodedHash | null;
    let error: Auth0Error | undefined;

    try {
      hash = await this.checkManagementSession();
    } catch (err) {
      error = err;
      hash = null;
    }

    runInAction(() => {
      if (hash) {
        this.managementToken = hash.accessToken!;
        this.managementExpiration = addSeconds(new Date(), hash.expiresIn!);
      } else {
        logger.error(
          `Failed to fetch the management API token: ${
            error ? errorMessage(error) : "no error"
          }. Won't be able to update user profile.`
        );
        this.managementToken = "";
        this.managementExpiration = new Date(0);
        this.managementRefreshFailed = true;
      }
    });
  }

  private parseHash() {
    return new Promise<Auth0DecodedHash | null>((resolve, reject) => {
      this.webAuth.parseHash((err, result) => (err ? reject(err) : resolve(result)));
    });
  }

  private checkSession() {
    return new Promise<Auth0DecodedHash | null>((resolve, reject) => {
      this.webAuth.checkSession({}, (err, result) => (err ? reject(err) : resolve(result)));
    });
  }

  private async ensureValidHash(hash: Auth0DecodedHash | null, predicate: (hash: Auth0DecodedHash) => boolean) {
    if (!hash || predicate(hash)) {
      return hash;
    }

    for (let retry = 0; retry < 5 && hash && !predicate(hash); ++retry) {
      await delay(500);
      hash = await this.checkSession();
    }

    if (!hash) {
      logger.error("Logged out while polling for an up to date hash");
      return null;
    }
    if (!predicate(hash)) {
      logger.error("Max hash retries reached, still out of date");
      return null;
    }

    return hash;
  }

  private checkManagementSession() {
    return new Promise<Auth0DecodedHash | null>((resolve, reject) => {
      this.webAuth.checkSession(
        {
          audience: `https://${this.options.domain}/api/v2/`,
          scope: "read:current_user update:current_user_metadata",
        },
        (err, result) => (err ? reject(err) : resolve(result))
      );
    });
  }
}

export function isAuth0Error(error: any): error is Auth0Error {
  // Auth0 sometimes returns the error code as .error, sometimes as .code...
  return typeof error.error === "string" || typeof error.code === "string";
}

export function auth0ErrorCode(error: Auth0Error) {
  return error.error || error.code || "";
}

export function auth0ErrorDescription(error: Auth0Error) {
  return error.errorDescription || error.error_description || error.description || "";
}

export type ErrorHandler<E, T> = (error: E) => T | never;
export type Auth0ErrorHandler<T> = ErrorHandler<Auth0Error, T>;
export interface Auth0ErrorHandlers<T> {
  [code: string]: Auth0ErrorHandler<T> | undefined;
}

export function defaultErrorHandler(error: any): never {
  throw error;
}

export function handleAuth0Error<T = void>(
  error: Auth0Error,
  handlers: Auth0ErrorHandlers<T>,
  fallback: Auth0ErrorHandler<T> = defaultErrorHandler
) {
  const code = auth0ErrorCode(error);
  const handler = handlers[code] || fallback;
  return handler(error);
}

export function handleError<T = void>(
  error: any,
  handlers: Auth0ErrorHandlers<T>,
  fallback: ErrorHandler<any, T> = defaultErrorHandler
) {
  if (isAuth0Error(error)) {
    return handleAuth0Error(error, handlers, fallback);
  } else {
    return fallback(error);
  }
}

function soon() {
  // soon = in one minute
  return now() + 60_000;
}

function errorMessage(error: Auth0Error | Error) {
  if (error instanceof Error) {
    return error.toString();
  }

  return error.error_description || error.errorDescription || error.description || error.error || "Auth0 error";
}
